import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import type { RootState } from '../index';
import { getABTestEndpoint, apiConfig } from '../../config/api';
import { logger } from '../../utils/logger';

// Types for dashboard personalization
export interface DashboardLayout {
  id: string;
  name: string;
  sections: DashboardSection[];
  priority: number;
  isDefault: boolean;
  abTestVariant?: string;
}

export interface DashboardSection {
  id: string;
  type:
    | 'greeting'
    | 'focus'
    | 'progress'
    | 'recommendations'
    | 'quick_actions'
    | 'achievements'
    | 'social'
    | 'tips';
  title: string;
  priority: number;
  visible: boolean;
  collapsed: boolean;
  position: number;
  customization: SectionCustomization;
  loadingStrategy: 'eager' | 'lazy' | 'on_demand';
  cacheStrategy: 'memory' | 'persistent' | 'none';
}

export interface SectionCustomization {
  showExplanations: boolean;
  showConfidenceIndicators: boolean;
  maxItems: number;
  sortBy: 'priority' | 'recency' | 'engagement' | 'ai_score';
  filterBy: string[];
  displayStyle: 'card' | 'list' | 'compact' | 'detailed';
}

export interface UserPreferences {
  userId: string;
  layoutId: string;
  sectionPreferences: Record<string, SectionCustomization>;
  interactionPatterns: InteractionPattern[];
  contentPreferences: ContentPreference[];
  accessibilitySettings: AccessibilitySettings;
  performanceSettings: PerformanceSettings;
  lastUpdated: string;
}

export interface InteractionPattern {
  sectionId: string;
  actionType: string;
  frequency: number;
  lastInteraction: string;
  engagementScore: number;
  timeOfDay: number[];
  dayOfWeek: number[];
}

export interface ContentPreference {
  contentType: string;
  preference: number; // -1 to 1
  reason: string;
  confidence: number;
  lastUpdated: string;
}

export interface AccessibilitySettings {
  highContrast: boolean;
  largeText: boolean;
  reduceMotion: boolean;
  screenReader: boolean;
  voiceNavigation: boolean;
  simplifiedExplanations: boolean;
}

export interface PerformanceSettings {
  lazyLoadingEnabled: boolean;
  imageOptimization: boolean;
  animationsEnabled: boolean;
  prefetchingEnabled: boolean;
  cacheSize: number;
  backgroundSync: boolean;
}

export interface ContextualTip {
  id: string;
  title: string;
  content: string;
  type: 'onboarding' | 'feature' | 'improvement' | 'troubleshooting';
  priority: number;
  triggers: TipTrigger[];
  conditions: TipCondition[];
  dismissible: boolean;
  maxShows: number;
  currentShows: number;
  lastShown?: string;
}

export interface TipTrigger {
  event: string;
  conditions: Record<string, any>;
  delay?: number;
}

export interface TipCondition {
  type:
    | 'journey_stage'
    | 'skill_level'
    | 'usage_pattern'
    | 'time_based'
    | 'feature_usage';
  operator:
    | 'equals'
    | 'greater_than'
    | 'less_than'
    | 'contains'
    | 'not_contains';
  value: any;
}

export interface ABTestVariant {
  id: string;
  name: string;
  description: string;
  layoutChanges: Partial<DashboardLayout>;
  trafficAllocation: number;
  isActive: boolean;
  metrics: ABTestMetrics;
}

export interface ABTestMetrics {
  impressions: number;
  interactions: number;
  conversionRate: number;
  engagementScore: number;
  retentionRate: number;
  userSatisfaction: number;
}

export interface ContentPrioritization {
  algorithm: 'engagement_based' | 'ai_score' | 'recency' | 'hybrid';
  weights: {
    aiConfidence: number;
    userEngagement: number;
    recency: number;
    personalRelevance: number;
    socialProof: number;
  };
  boosts: ContentBoost[];
  penalties: ContentPenalty[];
}

export interface ContentBoost {
  condition: string;
  multiplier: number;
  reason: string;
}

export interface ContentPenalty {
  condition: string;
  multiplier: number;
  reason: string;
}

interface DashboardPersonalizationState {
  userPreferences: UserPreferences | null;
  availableLayouts: DashboardLayout[];
  currentLayout: DashboardLayout | null;
  contextualTips: ContextualTip[];
  activeTips: ContextualTip[];
  abTestVariant: ABTestVariant | null;
  contentPrioritization: ContentPrioritization;
  interactionHistory: InteractionPattern[];
  loading: {
    preferences: boolean;
    layouts: boolean;
    tips: boolean;
    abTest: boolean;
  };
  error: string | null;
  performanceMetrics: {
    loadTimes: Record<string, number>;
    interactionLatency: Record<string, number>;
    cacheHitRate: number;
    memoryUsage: number;
  };
}

const initialState: DashboardPersonalizationState = {
  userPreferences: null,
  availableLayouts: [],
  currentLayout: null,
  contextualTips: [],
  activeTips: [],
  abTestVariant: null,
  contentPrioritization: {
    algorithm: 'hybrid',
    weights: {
      aiConfidence: 0.3,
      userEngagement: 0.25,
      recency: 0.15,
      personalRelevance: 0.2,
      socialProof: 0.1,
    },
    boosts: [],
    penalties: [],
  },
  interactionHistory: [],
  loading: {
    preferences: false,
    layouts: false,
    tips: false,
    abTest: false,
  },
  error: null,
  performanceMetrics: {
    loadTimes: {},
    interactionLatency: {},
    cacheHitRate: 0,
    memoryUsage: 0,
  },
};

// Default dashboard layouts
const defaultLayouts: DashboardLayout[] = [
  {
    id: 'default',
    name: 'Default Layout',
    priority: 1,
    isDefault: true,
    sections: [
      {
        id: 'greeting',
        type: 'greeting',
        title: 'Welcome',
        priority: 1,
        visible: true,
        collapsed: false,
        position: 0,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: false,
          maxItems: 1,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'card',
        },
      },
      {
        id: 'focus',
        type: 'focus',
        title: "Today's Focus",
        priority: 2,
        visible: true,
        collapsed: false,
        position: 1,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 1,
          sortBy: 'ai_score',
          filterBy: [],
          displayStyle: 'card',
        },
      },
      {
        id: 'progress',
        type: 'progress',
        title: 'Your Progress',
        priority: 3,
        visible: true,
        collapsed: false,
        position: 2,
        loadingStrategy: 'lazy',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: false,
          maxItems: 3,
          sortBy: 'recency',
          filterBy: [],
          displayStyle: 'card',
        },
      },
      {
        id: 'recommendations',
        type: 'recommendations',
        title: 'Recommended for You',
        priority: 4,
        visible: true,
        collapsed: false,
        position: 3,
        loadingStrategy: 'lazy',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 3,
          sortBy: 'ai_score',
          filterBy: [],
          displayStyle: 'card',
        },
      },
      {
        id: 'quick_actions',
        type: 'quick_actions',
        title: 'Quick Actions',
        priority: 5,
        visible: true,
        collapsed: false,
        position: 4,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 6,
          sortBy: 'engagement',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
    ],
  },
  {
    id: 'beginner_focused',
    name: 'Beginner Focused',
    priority: 2,
    isDefault: false,
    sections: [
      {
        id: 'greeting',
        type: 'greeting',
        title: 'Welcome',
        priority: 1,
        visible: true,
        collapsed: false,
        position: 0,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: false,
          maxItems: 1,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'detailed',
        },
      },
      {
        id: 'tips',
        type: 'tips',
        title: 'Learning Tips',
        priority: 2,
        visible: true,
        collapsed: false,
        position: 1,
        loadingStrategy: 'eager',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: false,
          maxItems: 2,
          sortBy: 'priority',
          filterBy: ['beginner'],
          displayStyle: 'detailed',
        },
      },
      {
        id: 'focus',
        type: 'focus',
        title: "Today's Learning",
        priority: 3,
        visible: true,
        collapsed: false,
        position: 2,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 1,
          sortBy: 'ai_score',
          filterBy: ['beginner', 'fundamentals'],
          displayStyle: 'detailed',
        },
      },
      {
        id: 'progress',
        type: 'progress',
        title: 'Your Journey',
        priority: 4,
        visible: true,
        collapsed: false,
        position: 3,
        loadingStrategy: 'lazy',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: false,
          maxItems: 2,
          sortBy: 'recency',
          filterBy: [],
          displayStyle: 'detailed',
        },
      },
      {
        id: 'recommendations',
        type: 'recommendations',
        title: 'Next Steps',
        priority: 5,
        visible: true,
        collapsed: false,
        position: 4,
        loadingStrategy: 'lazy',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 2,
          sortBy: 'ai_score',
          filterBy: ['beginner', 'easy'],
          displayStyle: 'detailed',
        },
      },
    ],
  },
  {
    id: 'advanced_compact',
    name: 'Advanced Compact',
    priority: 3,
    isDefault: false,
    sections: [
      {
        id: 'greeting',
        type: 'greeting',
        title: 'Welcome Back',
        priority: 1,
        visible: true,
        collapsed: false,
        position: 0,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 1,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'quick_actions',
        type: 'quick_actions',
        title: 'Quick Actions',
        priority: 2,
        visible: true,
        collapsed: false,
        position: 1,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 8,
          sortBy: 'engagement',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'recommendations',
        type: 'recommendations',
        title: 'Recommendations',
        priority: 3,
        visible: true,
        collapsed: false,
        position: 2,
        loadingStrategy: 'lazy',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: true,
          maxItems: 5,
          sortBy: 'ai_score',
          filterBy: ['advanced', 'expert'],
          displayStyle: 'list',
        },
      },
      {
        id: 'progress',
        type: 'progress',
        title: 'Progress',
        priority: 4,
        visible: true,
        collapsed: true,
        position: 3,
        loadingStrategy: 'on_demand',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 5,
          sortBy: 'recency',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'achievements',
        type: 'achievements',
        title: 'Recent Achievements',
        priority: 5,
        visible: true,
        collapsed: true,
        position: 4,
        loadingStrategy: 'on_demand',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 3,
          sortBy: 'recency',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
    ],
  },
];

// Async thunks
export const fetchUserPreferences = createAsyncThunk(
  'dashboardPersonalization/fetchUserPreferences',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/dashboard/preferences/${userId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch user preferences: ${response.statusText}`
        );
      }
      const preferences: UserPreferences = await response.json();
      return preferences;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch user preferences'
      );
    }
  }
);

export const updateUserPreferences = createAsyncThunk(
  'dashboardPersonalization/updateUserPreferences',
  async (preferences: Partial<UserPreferences>, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(
        `/api/dashboard/preferences/${preferences.userId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(preferences),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Failed to update user preferences: ${response.statusText}`
        );
      }
      const updatedPreferences: UserPreferences = await response.json();
      return updatedPreferences;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to update user preferences'
      );
    }
  }
);

export const fetchContextualTips = createAsyncThunk(
  'dashboardPersonalization/fetchContextualTips',
  async (
    {
      userId,
      journeyStage,
      skillLevel,
    }: { userId: string; journeyStage: string; skillLevel: string },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(
        `/api/dashboard/tips/${userId}?journeyStage=${journeyStage}&skillLevel=${skillLevel}`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch contextual tips: ${response.statusText}`
        );
      }
      const tips: ContextualTip[] = await response.json();
      return tips;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch contextual tips'
      );
    }
  }
);

export const assignABTestVariant = createAsyncThunk(
  'dashboardPersonalization/assignABTestVariant',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Call the Next.js web server API endpoint
      const response = await fetch(getABTestEndpoint(userId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(
          `Failed to assign A/B test variant: ${response.statusText}`
        );
      }
      const variant: ABTestVariant = await response.json();
      return variant;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to assign A/B test variant'
      );
    }
  }
);

export const trackInteraction = createAsyncThunk(
  'dashboardPersonalization/trackInteraction',
  async (
    {
      userId,
      sectionId,
      actionType,
      metadata,
    }: {
      userId: string;
      sectionId: string;
      actionType: string;
      metadata?: Record<string, any>;
    },
    { rejectWithValue }
  ) => {
    try {
      // For React Native Web, use local storage for interaction tracking
      // This avoids the need for an external API server during development
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Store interaction in localStorage for web platform
        const interaction = {
          userId,
          sectionId,
          actionType,
          metadata,
          timestamp: new Date().toISOString(),
        };

        // Get existing interactions from localStorage
        const existingInteractions = JSON.parse(
          localStorage.getItem('dashboard_interactions') || '[]'
        );

        // Add new interaction
        existingInteractions.push(interaction);

        // Store back to localStorage (keep last 100 interactions)
        const recentInteractions = existingInteractions.slice(-100);
        localStorage.setItem(
          'dashboard_interactions',
          JSON.stringify(recentInteractions)
        );

        logger.log('Interaction tracked locally:', interaction);
        return { sectionId, actionType, timestamp: new Date().toISOString() };
      }

      // For native platforms, try to use the API
      const response = await fetch(
        `${apiConfig.web.endpoints.dashboard}/interactions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            sectionId,
            actionType,
            metadata,
            timestamp: new Date().toISOString(),
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to track interaction: ${response.statusText}`);
      }
      return { sectionId, actionType, timestamp: new Date().toISOString() };
    } catch (error: any) {
      // Don't fail the UI for tracking errors
      logger.warn('Failed to track interaction:', error);
      return { sectionId, actionType, timestamp: new Date().toISOString() };
    }
  }
);

const dashboardPersonalizationSlice = createSlice({
  name: 'dashboardPersonalization',
  initialState: {
    ...initialState,
    availableLayouts: defaultLayouts,
    currentLayout: defaultLayouts[0],
  },
  reducers: {
    setCurrentLayout: (state, action: PayloadAction<string>) => {
      const layout = state.availableLayouts.find(l => l.id === action.payload);
      if (layout) {
        state.currentLayout = layout;
      }
    },
    updateSectionCustomization: (
      state,
      action: PayloadAction<{
        sectionId: string;
        customization: Partial<SectionCustomization>;
      }>
    ) => {
      if (state.currentLayout) {
        const section = state.currentLayout.sections.find(
          s => s.id === action.payload.sectionId
        );
        if (section) {
          section.customization = {
            ...section.customization,
            ...action.payload.customization,
          };
        }
      }
      if (state.userPreferences) {
        state.userPreferences.sectionPreferences[action.payload.sectionId] = {
          ...state.userPreferences.sectionPreferences[action.payload.sectionId],
          ...action.payload.customization,
        };
      }
    },
    toggleSectionVisibility: (state, action: PayloadAction<string>) => {
      if (state.currentLayout) {
        const section = state.currentLayout.sections.find(
          s => s.id === action.payload
        );
        if (section) {
          section.visible = !section.visible;
        }
      }
    },
    toggleSectionCollapsed: (state, action: PayloadAction<string>) => {
      if (state.currentLayout) {
        const section = state.currentLayout.sections.find(
          s => s.id === action.payload
        );
        if (section) {
          section.collapsed = !section.collapsed;
        }
      }
    },
    reorderSections: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>
    ) => {
      if (state.currentLayout) {
        const { fromIndex, toIndex } = action.payload;
        const sections = [...state.currentLayout.sections];
        const [movedSection] = sections.splice(fromIndex, 1);
        sections.splice(toIndex, 0, movedSection);

        // Update positions
        sections.forEach((section, index) => {
          section.position = index;
        });

        state.currentLayout.sections = sections;
      }
    },
    dismissTip: (state, action: PayloadAction<string>) => {
      const tip = state.contextualTips.find(t => t.id === action.payload);
      if (tip) {
        tip.currentShows += 1;
        tip.lastShown = new Date().toISOString();
      }
      state.activeTips = state.activeTips.filter(t => t.id !== action.payload);
    },
    addInteractionPattern: (
      state,
      action: PayloadAction<InteractionPattern>
    ) => {
      const existingIndex = state.interactionHistory.findIndex(
        p =>
          p.sectionId === action.payload.sectionId &&
          p.actionType === action.payload.actionType
      );

      if (existingIndex >= 0) {
        state.interactionHistory[existingIndex] = {
          ...state.interactionHistory[existingIndex],
          frequency: state.interactionHistory[existingIndex].frequency + 1,
          lastInteraction: action.payload.lastInteraction,
          engagementScore: action.payload.engagementScore,
        };
      } else {
        state.interactionHistory.push(action.payload);
      }
    },
    updateContentPrioritization: (
      state,
      action: PayloadAction<Partial<ContentPrioritization>>
    ) => {
      state.contentPrioritization = {
        ...state.contentPrioritization,
        ...action.payload,
      };
    },
    updatePerformanceMetrics: (
      state,
      action: PayloadAction<{
        metric: string;
        value: number;
        category?: string;
      }>
    ) => {
      const { metric, value, category } = action.payload;

      if (category === 'loadTime') {
        state.performanceMetrics.loadTimes[metric] = value;
      } else if (category === 'interaction') {
        state.performanceMetrics.interactionLatency[metric] = value;
      } else if (metric === 'cacheHitRate') {
        state.performanceMetrics.cacheHitRate = value;
      } else if (metric === 'memoryUsage') {
        state.performanceMetrics.memoryUsage = value;
      }
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch User Preferences
    builder
      .addCase(fetchUserPreferences.pending, state => {
        state.loading.preferences = true;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.loading.preferences = false;
        state.userPreferences = action.payload;

        // Apply user's preferred layout
        const preferredLayout = state.availableLayouts.find(
          l => l.id === action.payload.layoutId
        );
        if (preferredLayout) {
          state.currentLayout = preferredLayout;
        }
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.loading.preferences = false;
        state.error = action.payload as string;
      });

    // Update User Preferences
    builder
      .addCase(updateUserPreferences.pending, state => {
        state.loading.preferences = true;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.loading.preferences = false;
        state.userPreferences = action.payload;
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.loading.preferences = false;
        state.error = action.payload as string;
      });

    // Fetch Contextual Tips
    builder
      .addCase(fetchContextualTips.pending, state => {
        state.loading.tips = true;
      })
      .addCase(fetchContextualTips.fulfilled, (state, action) => {
        state.loading.tips = false;
        state.contextualTips = action.payload;

        // Filter active tips based on conditions
        state.activeTips = action.payload.filter(
          tip =>
            tip.currentShows < tip.maxShows &&
            (!tip.lastShown ||
              new Date().getTime() - new Date(tip.lastShown).getTime() >
                24 * 60 * 60 * 1000) // 24 hours
        );
      })
      .addCase(fetchContextualTips.rejected, (state, action) => {
        state.loading.tips = false;
        state.error = action.payload as string;
      });

    // Assign A/B Test Variant
    builder
      .addCase(assignABTestVariant.pending, state => {
        state.loading.abTest = true;
      })
      .addCase(assignABTestVariant.fulfilled, (state, action) => {
        state.loading.abTest = false;
        state.abTestVariant = action.payload;

        // Apply A/B test layout changes if any
        if (action.payload.layoutChanges && state.currentLayout) {
          state.currentLayout = {
            ...state.currentLayout,
            ...action.payload.layoutChanges,
          };
        }
      })
      .addCase(assignABTestVariant.rejected, (state, action) => {
        state.loading.abTest = false;
        state.error = action.payload as string;
      });

    // Track Interaction (no state changes needed)
    builder.addCase(trackInteraction.fulfilled, () => {
      // Interaction tracked successfully
    });
  },
});

export const {
  setCurrentLayout,
  updateSectionCustomization,
  toggleSectionVisibility,
  toggleSectionCollapsed,
  reorderSections,
  dismissTip,
  addInteractionPattern,
  updateContentPrioritization,
  updatePerformanceMetrics,
  clearError,
} = dashboardPersonalizationSlice.actions;

// Selectors
export const selectCurrentLayout = (state: RootState) =>
  state.dashboardPersonalization.currentLayout;

export const selectAvailableLayouts = (state: RootState) =>
  state.dashboardPersonalization.availableLayouts;

export const selectUserPreferences = (state: RootState) =>
  state.dashboardPersonalization.userPreferences;

export const selectContextualTips = (state: RootState) =>
  state.dashboardPersonalization.contextualTips;

export const selectActiveTips = (state: RootState) =>
  state.dashboardPersonalization.activeTips;

export const selectABTestVariant = (state: RootState) =>
  state.dashboardPersonalization.abTestVariant;

export const selectContentPrioritization = (state: RootState) =>
  state.dashboardPersonalization.contentPrioritization;

export const selectInteractionHistory = (state: RootState) =>
  state.dashboardPersonalization.interactionHistory;

export const selectPersonalizationLoading = (state: RootState) =>
  state.dashboardPersonalization.loading;

export const selectPersonalizationError = (state: RootState) =>
  state.dashboardPersonalization.error;

export const selectPerformanceMetrics = (state: RootState) =>
  state.dashboardPersonalization.performanceMetrics;

// Memoized selectors
export const selectVisibleSections = createSelector(
  [selectCurrentLayout],
  layout => {
    if (!layout) return [];
    return layout.sections
      .filter(section => section.visible)
      .sort((a, b) => a.position - b.position);
  }
);

export const selectEagerLoadSections = createSelector(
  [selectVisibleSections],
  sections => {
    return sections.filter(section => section.loadingStrategy === 'eager');
  }
);

export const selectLazyLoadSections = createSelector(
  [selectVisibleSections],
  sections => {
    return sections.filter(section => section.loadingStrategy === 'lazy');
  }
);

export const selectOnDemandSections = createSelector(
  [selectVisibleSections],
  sections => {
    return sections.filter(section => section.loadingStrategy === 'on_demand');
  }
);

export const selectHighPriorityTips = createSelector(
  [selectActiveTips],
  tips => {
    return tips
      .filter(tip => tip.priority >= 0.7)
      .sort((a, b) => b.priority - a.priority);
  }
);

export const selectSectionsByType = createSelector(
  [selectVisibleSections],
  sections => {
    return sections.reduce(
      (acc, section) => {
        if (!acc[section.type]) {
          acc[section.type] = [];
        }
        acc[section.type].push(section);
        return acc;
      },
      {} as Record<string, DashboardSection[]>
    );
  }
);

export const selectMostEngagedSections = createSelector(
  [selectInteractionHistory],
  history => {
    return history
      .sort((a, b) => b.engagementScore - a.engagementScore)
      .slice(0, 5);
  }
);

export const selectOptimalLayoutForUser = createSelector(
  [selectAvailableLayouts, selectInteractionHistory, selectUserPreferences],
  (layouts, history, preferences) => {
    if (preferences?.layoutId) {
      return layouts.find(l => l.id === preferences.layoutId) || layouts[0];
    }

    // Simple heuristic: if user has high engagement with tips, suggest beginner layout
    const tipEngagement =
      history.find(h => h.sectionId === 'tips')?.engagementScore || 0;
    if (tipEngagement > 0.7) {
      return layouts.find(l => l.id === 'beginner_focused') || layouts[0];
    }

    // If user has high engagement overall, suggest compact layout
    const avgEngagement =
      history.reduce((sum, h) => sum + h.engagementScore, 0) / history.length;
    if (avgEngagement > 0.8) {
      return layouts.find(l => l.id === 'advanced_compact') || layouts[0];
    }

    return layouts[0]; // Default layout
  }
);

export default dashboardPersonalizationSlice.reducer;
